import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';
import { sanitizeRichText } from '@/lib/sanitizeHtml';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { MapPin, Calendar, Users, Camera, Globe, ArrowLeft, User, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { trackWorkshopEvent } from '@/utils/workshopAnalytics';

interface InstructorData {
  id: string;
  business_name: string;
  bio: string | null;
  full_bio: string | null;
  profile_photo_url: string | null;
  website_url: string | null;
  slug: string | null;
}

interface InstructorPhoto {
  id: string;
  photo_url: string;
  is_featured: boolean;
  sort_order: number;
}

interface WorkshopListing {
  id: string;
  slug: string;
  title: string;
  location_name: string;
  price_display: string | null;
  max_participants: number | null;
  workshop_type: string;
  workshop_dates: Array<{ start_date: string; end_date: string }>;
  workshop_photos: Array<{ photo_url: string; sort_order: number }>;
}

const TYPE_LABELS: Record<string, string> = {
  astrophotography: 'Astrophotography',
  deep_sky: 'Deep Sky',
  landscape: 'Landscape',
  nature: 'Nature',
  mixed: 'Mixed',
};

export default function InstructorProfile() {
  const { slug } = useParams<{ slug: string }>();
  const [instructor, setInstructor] = useState<InstructorData | null>(null);
  const [workshops, setWorkshops] = useState<WorkshopListing[]>([]);
  const [photos, setPhotos] = useState<InstructorPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    if (slug) loadInstructor(slug);
  }, [slug]);

  // Track profile view once instructor is loaded
  useEffect(() => {
    if (instructor) {
      trackWorkshopEvent('profile_view', { leaderId: instructor.id, source: 'direct' });
    }
  }, [instructor?.id]);

  const loadInstructor = async (slugParam: string) => {
    setLoading(true);
    try {
      const { data: leader, error } = await supabase
        .from('workshop_leaders')
        .select('id, business_name, bio, full_bio, profile_photo_url, website_url, slug')
        .eq('slug', slugParam)
        .eq('is_active', true)
        .single();

      if (error || !leader) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setInstructor(leader);

      // Load their active workshops
      const now = new Date().toISOString().split('T')[0];
      const { data: listings } = await supabase
        .from('workshop_listings')
        .select(`
          id, slug, title, location_name, price_display, max_participants, workshop_type,
          workshop_dates(start_date, end_date),
          workshop_photos(photo_url, sort_order)
        `)
        .eq('leader_id', leader.id)
        .eq('is_active', true);

      // Load gallery photos
      const { data: photoData } = await supabase
        .from('instructor_photos')
        .select('*')
        .eq('leader_id', leader.id)
        .order('sort_order');
      setPhotos(photoData || []);

      // Filter to workshops with future dates, sort by next date
      const withFuture = (listings || [])
        .filter(l => l.workshop_dates?.some((d: { end_date: string }) => d.end_date >= now))
        .sort((a, b) => {
          const nextA = getNextDate(a.workshop_dates)?.start_date || '9999';
          const nextB = getNextDate(b.workshop_dates)?.start_date || '9999';
          return nextA.localeCompare(nextB);
        });

      setWorkshops(withFuture);
    } catch (err) {
      console.error('Error loading instructor:', err);
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  };

  const getNextDate = (dates: Array<{ start_date: string; end_date: string }>) => {
    const now = new Date().toISOString().split('T')[0];
    return dates
      .filter(d => d.end_date >= now)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))[0];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
          <div className="flex items-start gap-8">
            <Skeleton className="w-32 h-32 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-4">
              <Skeleton className="h-8 w-64" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !instructor) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <User className="w-16 h-16 text-muted-foreground/20 mx-auto mb-4" />
          <h1 className="text-2xl font-serif font-bold text-foreground mb-2">Instructor not found</h1>
          <p className="text-muted-foreground mb-6">This profile doesn't exist or is no longer active.</p>
          <Link to="/workshops/instructors">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Instructors
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={`${instructor.business_name} — Astrophotography Instructor | Milky Way Planner`}
        description={instructor.bio || `View ${instructor.business_name}'s astrophotography workshop profile and upcoming workshops.`}
      />
      <div className="min-h-screen bg-background">
        {/* Header */}
        <section className="bg-[#0f1923] text-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
            <Link
              to="/workshops/instructors"
              className="inline-flex items-center gap-1.5 text-sm text-white/50 hover:text-white transition-colors mb-8"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              All Instructors
            </Link>

            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Photo */}
              {instructor.profile_photo_url ? (
                <img
                  src={instructor.profile_photo_url}
                  alt={instructor.business_name}
                  className="w-32 h-32 rounded-full object-cover border-2 border-white/20 flex-shrink-0"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                  <User className="w-12 h-12 text-white/30" />
                </div>
              )}

              <div className="text-center sm:text-left">
                <h1 className="font-serif text-3xl sm:text-4xl font-bold tracking-tight mb-2">
                  {instructor.business_name}
                </h1>
                {instructor.bio && (
                  <p className="text-white/70 text-lg leading-relaxed max-w-2xl">
                    {instructor.bio}
                  </p>
                )}
                <div className="flex items-center gap-4 mt-3 flex-wrap">
                  {instructor.website_url && (
                    <a
                      href={instructor.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#c9922a] hover:text-[#d4a43a] transition-colors text-sm"
                      onClick={() => trackWorkshopEvent('website_click', {
                        leaderId: instructor.id,
                        source: 'instructor_profile',
                        metadata: { url: instructor.website_url },
                        allowDuplicate: true,
                      })}
                    >
                      <Globe className="w-3.5 h-3.5" />
                      {instructor.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                    </a>
                  )}
                  {workshops.length > 0 && (
                    <span className="text-white/50 text-sm">
                      {workshops.length} upcoming workshop{workshops.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Two-column body */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Left column: Bio */}
            <div className="lg:col-span-2">
              {instructor.full_bio && (
                <div className="mb-10">
                  <h2 className="font-serif text-2xl font-bold text-foreground mb-4">About</h2>
                  <div
                    className="prose prose-sm max-w-none text-muted-foreground leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: sanitizeRichText(instructor.full_bio) }}
                  />
                </div>
              )}

              {/* Workshops */}
              <div>
                <h2 className="font-serif text-2xl font-bold text-foreground mb-6">
                  {workshops.length > 0 ? `Upcoming Workshops (${workshops.length})` : 'Workshops'}
                </h2>

                {workshops.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {workshops.map(workshop => {
                      const heroPhoto = workshop.workshop_photos?.sort((a, b) => a.sort_order - b.sort_order)[0];
                      const nextDate = getNextDate(workshop.workshop_dates || []);

                      return (
                        <Link key={workshop.id} to={`/workshops/${workshop.slug}`} className="group">
                          <Card className="overflow-hidden h-full hover:shadow-lg transition-shadow rounded-[4px]">
                            <div className="h-40 overflow-hidden bg-[#0f1923]">
                              {heroPhoto ? (
                                <img
                                  src={heroPhoto.photo_url}
                                  alt={workshop.title}
                                  className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Camera className="w-10 h-10 text-white/20" />
                                </div>
                              )}
                            </div>
                            <CardContent className="p-4 space-y-2">
                              <Badge variant="outline" className="text-xs">
                                {TYPE_LABELS[workshop.workshop_type] || workshop.workshop_type}
                              </Badge>
                              <h3 className="font-serif font-bold text-foreground leading-tight group-hover:text-[#c9922a] transition-colors">
                                {workshop.title}
                              </h3>
                              <div className="space-y-1 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1.5">
                                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                                  <span>{workshop.location_name}</span>
                                </div>
                                {nextDate && (
                                  <div className="flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 flex-shrink-0" />
                                    <span>
                                      {format(parseISO(nextDate.start_date), 'MMM d')} – {format(parseISO(nextDate.end_date), 'MMM d, yyyy')}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center justify-between pt-2 border-t">
                                {workshop.price_display && (
                                  <span className="font-semibold text-foreground text-sm">{workshop.price_display}</span>
                                )}
                                {workshop.max_participants && (
                                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                                    <Users className="w-3 h-3" />
                                    {workshop.max_participants} max
                                  </span>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-muted/30 rounded-lg border border-border/50">
                    <Camera className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
                    <p className="text-muted-foreground">No upcoming workshops at this time.</p>
                    <Link to="/workshops" className="text-sm text-[#c9922a] hover:underline mt-2 inline-block">
                      Browse all workshops →
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Right column: Images */}
            {photos.length > 0 && (
              <div className="space-y-3">
                {/* Featured image — large */}
                {photos.filter(p => p.is_featured).map(photo => {
                  const idx = photos.indexOf(photo);
                  return (
                    <div
                      key={photo.id}
                      className="rounded-lg overflow-hidden border border-border shadow-sm cursor-pointer"
                      onClick={() => setLightboxIndex(idx)}
                    >
                      <img
                        src={photo.photo_url}
                        alt={`${instructor.business_name} featured`}
                        className="w-full aspect-[3/2] object-cover hover:opacity-90 transition-opacity"
                      />
                    </div>
                  );
                })}

                {/* Gallery grid */}
                {photos.filter(p => !p.is_featured).length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {photos.filter(p => !p.is_featured).map(photo => {
                      const idx = photos.indexOf(photo);
                      return (
                        <div
                          key={photo.id}
                          className="rounded-lg overflow-hidden border border-border cursor-pointer"
                          onClick={() => setLightboxIndex(idx)}
                        >
                          <img
                            src={photo.photo_url}
                            alt=""
                            className="w-full aspect-square object-cover hover:opacity-90 transition-opacity"
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && photos[lightboxIndex] && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxIndex(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/70 hover:text-white z-10"
            onClick={() => setLightboxIndex(null)}
          >
            <X className="w-8 h-8" />
          </button>
          {photos.length > 1 && (
            <>
              <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((lightboxIndex - 1 + photos.length) % photos.length);
                }}
              >
                <ChevronLeft className="w-10 h-10" />
              </button>
              <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white z-10"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((lightboxIndex + 1) % photos.length);
                }}
              >
                <ChevronRight className="w-10 h-10" />
              </button>
            </>
          )}
          <img
            src={photos[lightboxIndex].photo_url}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 text-white/50 text-sm">
            {lightboxIndex + 1} / {photos.length}
          </div>
        </div>
      )}
    </>
  );
}
